import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Badge, Button, Callout, Card, DropdownMenu, ScrollArea, Text, Tooltip } from '@radix-ui/themes';
import { CaretDownIcon, CheckIcon, CodeIcon, InfoCircledIcon, PaperPlaneIcon, PlusIcon, QuoteIcon, TrashIcon } from '@radix-ui/react-icons';
import { generateId } from '../../util/utils';
import { useAuthStore, useEditorStore, useMovieStore } from '../../stores';
import { authFetch } from '../../util/authFetch';
import { handleUnauthorizedRedirect } from '../../util/authRedirect';
import './Editor.css';
import { TextArea } from '@radix-ui/themes';
import { useState, useEffect, useRef, useCallback, memo, lazy, Suspense } from 'react';
import { useGet, useAiActionExecutor } from '../../hooks';
import { PersonIcon } from '@radix-ui/react-icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useThemeStore } from '../../stores';
import { AIAssistantMessage } from './AIAssistantMessage';
import { parseAssistantResponse } from '../../util/aiResponseParser';
import { ensureMovieForChat } from '../../util/ensureMovieForChat';
import { sendChatMessageDirect } from '../../util/aiChatClient';
import { processAiProviderResponse } from '../../util/processAiProviderResponse';
import { ConfirmModal } from '../ui/ConfirmModal';
import { useTranslation } from '../../i18n';
import { ChatPromptWalker } from './ChatPromptWalker';
// Lazy load syntax highlighter to reduce initial bundle size
const SyntaxHighlighter = lazy(() => import('react-syntax-highlighter').then(mod => ({ default: mod.Prism })));
/** Memoized chat message component to prevent unnecessary re-renders */
const ChatMessageItem = memo(({ message, showDebug, onDelete, isDeleting, }) => {
    const { t } = useTranslation();
    const codeStyle = useThemeStore((state) => state.resolvedAppearance) === 'dark' ? oneDark : oneLight;
    const canDelete = Boolean(onDelete) && !isDeleting;
    const deleteButton = canDelete ? (_jsx(Tooltip, { content: t('aiChat.deleteMessage'), children: _jsx(Button, { variant: "ghost", size: "1", className: "user-chat-message-delete", "aria-label": t('aiChat.deleteMessage'), onClick: () => onDelete?.(message.id), children: _jsx(TrashIcon, { "aria-hidden": "true" }) }) })) : null;
    if (message.role === 'assistant') {
        const parsed = parseAssistantResponse(message.content);
        const debug = message.debug;
        const jobs = debug?.jobs ?? (showDebug ? parsed.jobs : []);
        const actions = debug?.actions ?? (showDebug ? parsed.actions : []);
        const payloads = debug?.payloads ?? (showDebug ? parsed.payloads : []);
        const executionResults = debug?.executionResults;
        const { thinking, response } = parsed;
        return (_jsxs("div", { className: 'user-chat-message user-chat-message-assistant', children: [_jsxs("div", { className: 'flex items-start gap-2 p-2 min-w-0 flex-1', children: [_jsx(Badge, { color: "gray", size: "3", children: "AI" }), _jsxs("div", { className: 'flex flex-col gap-1 min-w-0', children: [thinking && thinking.length > 0 && (_jsxs("details", { className: 'ai-think-block', children: [_jsx("summary", { className: 'ai-think-summary', children: t('aiChat.thinking') }), _jsx("div", { className: 'ai-think-content', children: thinking })] })), showDebug && jobs.length > 0 && (_jsxs("details", { className: 'ai-think-block', open: true, children: [_jsx("summary", { className: 'ai-jobs-summary', children: t('aiChat.jobs') }), jobs.map((job, index) => (_jsx("pre", { className: 'ai-jobs-content', children: JSON.stringify(job, null, 2) }, `job-${index}`)))] })), showDebug && actions.length > 0 && (_jsxs("details", { className: 'ai-think-block', open: true, children: [_jsx("summary", { className: 'ai-actions-summary', children: t('aiChat.actions') }), actions.map((action, index) => (_jsx("pre", { className: 'ai-actions-content', children: JSON.stringify(action, null, 2) }, `action-${index}`)))] })), showDebug && payloads.length > 0 && (_jsxs("details", { className: 'ai-think-block', open: true, children: [_jsx("summary", { className: 'ai-payloads-summary', children: t('aiChat.payloads') }), payloads.map((payload, index) => (_jsx("pre", { className: 'ai-payloads-content', children: JSON.stringify(payload, null, 2) }, `payload-${index}`)))] })), showDebug && executionResults && executionResults.length > 0 && (_jsxs("details", { className: 'ai-think-block', open: true, children: [_jsx("summary", { className: 'ai-debug-summary', children: t('aiChat.executionResults') }), executionResults.map((result, index) => (_jsx("pre", { className: 'ai-debug-content', children: JSON.stringify(result, null, 2) }, `exec-${index}`)))] })), _jsx("div", { className: 'ai-markdown-content text-theme-muted text-sm', children: _jsx(Suspense, { fallback: _jsx("div", { className: 'text-theme-muted text-xs', children: t('aiChat.loading') }), children: _jsx(ReactMarkdown, { remarkPlugins: [remarkGfm], components: {
                                                code({ className, children, ...props }) {
                                                    const match = /language-(\w+)/.exec(className || '');
                                                    const codeString = String(children).replace(/\n$/, '');
                                                    if (match) {
                                                        return (_jsx(Suspense, { fallback: _jsx("pre", { className: 'text-xs', children: codeString }), children: _jsx(SyntaxHighlighter, { style: codeStyle, language: match[1], PreTag: "div", wrapLongLines: true, customStyle: {
                                                                    margin: '0.5em 0',
                                                                    borderRadius: '6px',
                                                                    fontSize: '0.8rem',
                                                                    overflowX: 'hidden',
                                                                    whiteSpace: 'pre-wrap',
                                                                    wordBreak: 'break-word',
                                                                    overflowWrap: 'anywhere',
                                                                    maxWidth: '100%',
                                                                }, children: codeString }) }));
                                                    }
                                                    return (_jsx("code", { className: 'ai-inline-code', ...props, children: children }));
                                                },
                                                pre({ children }) {
                                                    return _jsx(_Fragment, { children: children });
                                                },
                                            }, children: response }) }) })] })] }), deleteButton] }));
    }
    return (_jsxs("div", { className: 'user-chat-message user-chat-message-user', children: [_jsxs("div", { className: 'flex items-center gap-2 p-2 min-w-0 flex-1', children: [_jsx(PersonIcon, { className: 'w-4 h-4 text-theme-primary shrink-0' }), _jsx("span", { className: 'text-theme-secondary text-sm', children: message.content })] }), deleteButton] }));
});
export function AIChat() {
    const { t } = useTranslation();
    const { aiChatVisible, aiChatText, setAiChatText, aiChatHistory, aiChatMovieId, aiChatWidth, setAiChatWidth, addAiChatMessage, removeAiChatMessage, setAiChatHistory, resetAiChat, setAiChatMovieId, aiChatConversations, aiChatActiveConversationId, setAiChatConversations, setAiChatActiveConversationId, addAiChatConversation, removeAiChatConversation, aiSamplePrompts, aiChatDebugMode, toggleAiChatDebugMode, } = useEditorStore();
    const { selectedMovieId, currentScene } = useMovieStore();
    const currentFrame = useEditorStore((state) => state.currentFrame);
    const isSystemUser = useAuthStore((state) => state.user?.role === 'system');
    const showDebug = aiChatDebugMode || isSystemUser;
    const [isResizing, setIsResizing] = useState(false);
    const [isEnsuringMovie, setIsEnsuringMovie] = useState(false);
    const [chatError, setChatError] = useState(null);
    const [resizeStartX, setResizeStartX] = useState(0);
    const [resizeStartWidth, setResizeStartWidth] = useState(0);
    const { executeActions } = useAiActionExecutor();
    const sidebarRef = useRef(null);
    const scrollAreaRef = useRef(null);
    const messagesEndRef = useRef(null);
    const prevMovieIdRef = useRef(aiChatMovieId);
    // Handle movie changes
    useEffect(() => {
        if (selectedMovieId === null) {
            resetAiChat();
            prevMovieIdRef.current = null;
            return;
        }
        if (selectedMovieId !== prevMovieIdRef.current) {
            const previousMovieId = prevMovieIdRef.current;
            prevMovieIdRef.current = selectedMovieId;
            const { aiChatMovieId: cachedMovieId, aiChatConversations: cachedConvs, aiChatHistory } = useEditorStore.getState();
            if (cachedMovieId === selectedMovieId && cachedConvs.length > 0) {
                return;
            }
            // Movie yokken başlayan sohbet: yeni movie bağlanırken mesajları koru
            if (previousMovieId === null && aiChatHistory.length > 0) {
                setAiChatMovieId(selectedMovieId);
                return;
            }
            resetAiChat();
            setAiChatMovieId(selectedMovieId);
        }
    }, [selectedMovieId, resetAiChat, setAiChatMovieId]);
    // Fetch conversations list for this movie (useGet clears stale data on URL change)
    const conversationsUrl = selectedMovieId
        ? `/api/movies/${selectedMovieId}/chat/conversations`
        : null;
    const { data: conversationsData, loading: isLoadingConversations } = useGet(conversationsUrl);
    // Load conversations from API
    useEffect(() => {
        if (conversationsData && selectedMovieId) {
            const conversations = conversationsData.map(c => ({
                id: c.id,
                title: c.title,
                createdAt: c.createdAt,
                lastMessageAt: c.lastMessageAt,
            }));
            setAiChatConversations(conversations);
            setAiChatMovieId(selectedMovieId);
            // Auto-select the first conversation if none is active or active one no longer exists
            const { aiChatActiveConversationId: activeId } = useEditorStore.getState();
            const activeExists = conversations.some(c => c.id === activeId);
            if ((!activeId || !activeExists) && conversations.length > 0) {
                setAiChatActiveConversationId(conversations[0].id);
            }
        }
    }, [conversationsData, selectedMovieId, setAiChatConversations, setAiChatMovieId, setAiChatActiveConversationId]);
    // Fetch history for the active conversation (URL change auto-triggers fetch)
    const chatHistoryUrl = selectedMovieId && aiChatActiveConversationId
        ? `/api/movies/${selectedMovieId}/chat/history?conversationId=${aiChatActiveConversationId}`
        : null;
    const { data: chatHistoryData, loading: isLoadingHistory } = useGet(chatHistoryUrl);
    // Load chat history from API response
    useEffect(() => {
        if (!selectedMovieId || !chatHistoryData || aiChatActiveConversationId === null) {
            return;
        }
        const history = chatHistoryData.map(msg => ({
            id: msg.id,
            role: msg.role,
            content: msg.content,
            timestamp: new Date(msg.timestamp),
        }));
        setAiChatHistory(history, selectedMovieId);
    }, [chatHistoryData, aiChatActiveConversationId, setAiChatHistory, selectedMovieId]);
    const [isSending, setIsSending] = useState(false);
    const [deletingMessageId, setDeletingMessageId] = useState(null);
    const [deleteConfirmMessageId, setDeleteConfirmMessageId] = useState(null);
    const handleRequestDeleteMessage = useCallback((messageId) => {
        if (deletingMessageId)
            return;
        setDeleteConfirmMessageId(messageId);
    }, [deletingMessageId]);
    const handleConfirmDeleteMessage = useCallback(async () => {
        const messageId = deleteConfirmMessageId;
        if (!messageId || !selectedMovieId || aiChatActiveConversationId === null || deletingMessageId)
            return;
        const numericId = Number.parseInt(messageId, 10);
        if (!Number.isFinite(numericId))
            return;
        setDeletingMessageId(messageId);
        setDeleteConfirmMessageId(null);
        try {
            const url = `/api/movies/${selectedMovieId}/chat/conversations/${aiChatActiveConversationId}/messages/${numericId}`;
            const response = await authFetch(url, { method: 'DELETE' });
            if (response.status === 401) {
                handleUnauthorizedRedirect();
                return;
            }
            if (response.ok) {
                removeAiChatMessage(messageId);
            }
            else {
                console.error('Failed to delete message:', response.status);
            }
        }
        catch (error) {
            console.error('Failed to delete message:', error);
        }
        finally {
            setDeletingMessageId(null);
        }
    }, [deleteConfirmMessageId, selectedMovieId, aiChatActiveConversationId, deletingMessageId, removeAiChatMessage]);
    const deleteConfirmMessage = deleteConfirmMessageId
        ? aiChatHistory.find((m) => m.id === deleteConfirmMessageId)
        : null;
    const deleteConfirmPreview = deleteConfirmMessage
        ? (deleteConfirmMessage.role === 'assistant'
            ? parseAssistantResponse(deleteConfirmMessage.content).response
            : deleteConfirmMessage.content).trim().slice(0, 120)
        : '';
    const handleArchiveConversation = useCallback(async (e, convId) => {
        e.stopPropagation();
        if (!selectedMovieId)
            return;
        try {
            const url = `/api/movies/${selectedMovieId}/chat/conversations/${convId}`;
            // Use fetch directly for dynamic URL delete
            const response = await authFetch(url, { method: 'PUT' });
            if (response.status === 401) {
                handleUnauthorizedRedirect();
                return;
            }
            if (response.ok) {
                removeAiChatConversation(convId);
            }
        }
        catch (error) {
            console.error('Failed to delete conversation:', error);
        }
    }, [selectedMovieId, removeAiChatConversation]);
    const handleTabClick = useCallback((convId) => {
        if (convId === aiChatActiveConversationId)
            return;
        setAiChatActiveConversationId(convId);
        setAiChatHistory([], selectedMovieId); // Clear while loading
    }, [aiChatActiveConversationId, setAiChatActiveConversationId, setAiChatHistory, selectedMovieId]);
    const handleNewChat = useCallback(() => {
        setAiChatActiveConversationId(null);
        setAiChatHistory([], selectedMovieId);
        setAiChatText('');
    }, [setAiChatActiveConversationId, setAiChatHistory, selectedMovieId, setAiChatText]);
    // Auto-scroll to bottom
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ block: 'end' });
    };
    useEffect(() => {
        requestAnimationFrame(() => {
            setTimeout(scrollToBottom, 500);
        });
    }, [aiChatHistory, isSending, aiChatVisible]);
    // Resize logic
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isResizing) {
                const dx = e.clientX - resizeStartX;
                const newWidth = Math.max(200, Math.min(800, resizeStartWidth + dx));
                setAiChatWidth(newWidth);
            }
        };
        const handleMouseUp = () => {
            setIsResizing(false);
        };
        if (isResizing) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing, resizeStartX, resizeStartWidth, setAiChatWidth]);
    const handleResizeMouseDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsResizing(true);
        setResizeStartX(e.clientX);
        setResizeStartWidth(aiChatWidth);
    };
    const sendPromptToAI = async () => {
        if (!aiChatText.trim() || isSending || isEnsuringMovie)
            return;
        const messageText = aiChatText.trim();
        setAiChatText('');
        setChatError(null);
        const userMessageId = generateId('user-msg');
        addAiChatMessage({
            id: userMessageId,
            role: 'user',
            content: messageText,
            timestamp: new Date(),
        });
        setIsEnsuringMovie(true);
        let movieId = null;
        try {
            movieId = await ensureMovieForChat(messageText);
        }
        catch (error) {
            console.error('[AIChat] ensureMovieForChat failed:', error);
        }
        finally {
            setIsEnsuringMovie(false);
        }
        if (!movieId) {
            setChatError('Could not create or load a project. Please try again.');
            return;
        }
        setIsSending(true);
        try {
            const sceneId = currentScene?.id ? parseInt(currentScene.id, 10) : null;
            const response = await sendChatMessageDirect(movieId, {
                message: messageText,
                conversationId: aiChatActiveConversationId,
                sceneId: Number.isFinite(sceneId) ? sceneId : null,
                currentFrame,
            });
            if (response) {
                const assistantMessage = await processAiProviderResponse(response, {
                    captureDebug: showDebug,
                    executeActions,
                });
                addAiChatMessage(assistantMessage);
                if (aiChatActiveConversationId === null && response.conversationId) {
                    const newConv = {
                        id: response.conversationId,
                        title: response.conversationTitle,
                        createdAt: new Date().toISOString(),
                        lastMessageAt: new Date().toISOString(),
                    };
                    addAiChatConversation(newConv);
                    setAiChatActiveConversationId(response.conversationId);
                }
            }
            else {
                setChatError('Failed to send message to AI.');
            }
        }
        catch (error) {
            console.error('Failed to send message to AI:', error);
            setChatError('Failed to send message to AI.');
        }
        finally {
            setIsSending(false);
        }
    };
    // Calculate dynamic rows based on text length
    const calculateRows = (text) => {
        const baseRows = 3;
        if (text.length > 500) {
            return baseRows * 2;
        }
        return baseRows;
    };
    // Check if we're in "new chat" mode (no active conversation)
    const isNewChat = aiChatActiveConversationId === null;
    const isLoadingChat = isLoadingConversations || isLoadingHistory || isEnsuringMovie;
    const activeConversationTitle = isNewChat
        ? 'New Chat'
        : (aiChatConversations.find((c) => c.id === aiChatActiveConversationId)?.title || 'New Chat');
    const showConversationDropdown = selectedMovieId !== null || aiChatConversations.length > 0;
    if (!aiChatVisible)
        return null;
    return (_jsxs("div", { ref: sidebarRef, className: "user-chat-sidebar", style: { width: `${aiChatWidth}px` }, children: [_jsx("div", { className: `user-chat-resize-handle${isResizing ? ' is-resizing' : ''}`, onMouseDown: handleResizeMouseDown, role: "separator", "aria-orientation": "vertical", "aria-label": t('aiChat.resizePanel') }), _jsxs("div", { className: 'user-chat-header', children: [showConversationDropdown ? (_jsx("div", { className: "user-chat-conversation-dropdown", children: _jsxs(DropdownMenu.Root, { children: [_jsx(DropdownMenu.Trigger, { children: _jsxs("button", { type: "button", className: "user-chat-conversation-trigger", "aria-label": t('aiChat.selectConversation'), children: [_jsx("span", { className: "user-chat-conversation-trigger-title", children: activeConversationTitle }), _jsx(CaretDownIcon, { "aria-hidden": "true", className: "user-chat-conversation-trigger-chevron" })] }) }), _jsxs(DropdownMenu.Content, { variant: "soft", size: "2", align: "start", side: "bottom", sideOffset: 0, collisionPadding: 8, className: "user-chat-conversation-menu", children: [_jsxs(DropdownMenu.Item, { className: isNewChat ? 'user-chat-conversation-item-active' : undefined, onSelect: handleNewChat, children: [isNewChat && _jsx(CheckIcon, { "aria-hidden": "true", className: "user-chat-conversation-check" }), _jsx("span", { className: "user-chat-conversation-item-title", children: t('aiChat.newChat') })] }), aiChatConversations.length > 0 && _jsx(DropdownMenu.Separator, {}), aiChatConversations.map((conv) => {
                                            const isActive = conv.id === aiChatActiveConversationId;
                                            return (_jsxs(DropdownMenu.Item, { className: isActive ? 'user-chat-conversation-item-active' : undefined, onSelect: () => handleTabClick(conv.id), children: [isActive && _jsx(CheckIcon, { "aria-hidden": "true", className: "user-chat-conversation-check" }), _jsx("span", { className: "user-chat-conversation-item-title", children: conv.title || 'New Chat' }), _jsx("button", { type: "button", className: "user-chat-conversation-item-delete", "aria-label": t('aiChat.deleteConversation'), onPointerDown: (e) => e.preventDefault(), onClick: (e) => void handleArchiveConversation(e, conv.id), children: _jsx(TrashIcon, { "aria-hidden": "true" }) })] }, conv.id));
                                        })] })] }) })) : (_jsx("div", { className: "user-chat-conversation-label", children: activeConversationTitle })), _jsxs("div", { className: 'user-chat-header-actions mr-2', children: [selectedMovieId !== null && (_jsx(Tooltip, { content: t('aiChat.newChatTooltip'), children: _jsx("button", { type: "button", className: "user-chat-header-action", "aria-label": t('aiChat.newChatTooltip'), onClick: handleNewChat, children: _jsx(PlusIcon, { "aria-hidden": "true" }) }) })), _jsx(Tooltip, { content: aiChatDebugMode ? t('aiChat.debugOn') : t('aiChat.debugOff'), children: _jsx("button", { type: "button", className: `user-chat-header-action${aiChatDebugMode ? ' is-active' : ''}`, "aria-pressed": aiChatDebugMode, "aria-label": aiChatDebugMode ? t('aiChat.debugOn') : t('aiChat.debugOff'), onClick: toggleAiChatDebugMode, children: _jsx(CodeIcon, { "aria-hidden": "true" }) }) })] })] }), selectedMovieId === null && !isLoadingChat && aiChatHistory.length === 0 && aiChatText.trim() === '' && _jsx("div", { className: "ai-welcome-message m-3", children: _jsxs(Callout.Root, { variant: "soft", color: "gray", children: [_jsx(Callout.Icon, { children: _jsx(InfoCircledIcon, {}) }), _jsx(Callout.Text, { children: _jsx(AIAssistantMessage, {}) })] }) }), _jsxs(ScrollArea, { size: "2", type: "auto", className: "user-chat-content", ref: scrollAreaRef, children: [isLoadingChat && (_jsxs("div", { className: 'chat-loading-container', children: [_jsx("div", { className: 'chat-loading-spinner' }), _jsx("span", { className: 'text-theme-muted text-sm mt-2', children: isEnsuringMovie ? 'Creating project...' : 'Loading chat...' })] })), chatError && (_jsx(Callout.Root, { variant: "soft", color: "red", className: "m-2", children: _jsx(Callout.Text, { children: chatError }) })), selectedMovieId === null && !isLoadingChat && aiChatHistory.length === 0 && (_jsxs("div", { className: 'flex gap-2', children: [_jsx("p", { className: 'text-theme-secondary p-2', children: "Here are some sample prompts to get you started:" }), _jsx("div", { className: 'flex flex-wrap gap-1', children: aiSamplePrompts.map((prompt) => (_jsx(Card, { variant: "surface", className: 'cursor-pointer m-1 hover:brightness-125', onClick: () => setAiChatText(prompt), children: _jsxs(Text, { as: "p", size: "3", children: [_jsx(QuoteIcon, { className: 'w-5 h-5 text-theme-secondary' }), _jsx(Text, { size: "3", className: 'text-theme-muted', children: prompt })] }) }, generateId('sample-prompt')))) })] })), aiChatHistory.map((message) => (_jsx(ChatMessageItem, { message: message, showDebug: showDebug, onDelete: aiChatActiveConversationId !== null ? handleRequestDeleteMessage : undefined, isDeleting: deletingMessageId === message.id }, message.id))), isSending && (_jsxs("div", { className: 'flex items-center gap-2 p-2', children: [_jsx(PersonIcon, { className: 'w-4 h-4 text-theme-primary' }), _jsxs("div", { className: 'typing-indicator', children: [_jsx("span", {}), _jsx("span", {}), _jsx("span", {})] })] })), _jsx("div", { className: "chat-prompt-walker-clearance", "aria-hidden": "true" }), _jsx("div", { ref: messagesEndRef })] }), _jsxs("div", { className: "user-chat-inputs", children: [_jsx(ChatPromptWalker, {}), _jsx(TextArea, { className: "user-chat-textarea", variant: "surface", size: "2", radius: "medium", value: aiChatText, onChange: (e) => setAiChatText(e.target.value), onKeyDown: (e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                void sendPromptToAI();
                            }
                        }, rows: calculateRows(aiChatText), placeholder: t('aiChat.placeholder'), autoFocus: true }), _jsx("div", { className: "user-chat-input-actions", children: _jsxs(Button, { className: "user-chat-send-button", variant: "soft", color: "gray", radius: "medium", size: "2", disabled: isSending || isEnsuringMovie || !aiChatText.trim(), onClick: () => void sendPromptToAI(), children: [_jsx(PaperPlaneIcon, { "aria-hidden": "true" }), _jsx("span", { children: t('aiChat.send') })] }) })] }), _jsx(ConfirmModal, { variant: "panel", open: deleteConfirmMessageId !== null, onOpenChange: (open) => {
                    if (!open)
                        setDeleteConfirmMessageId(null);
                }, title: "Delete message?", description: deleteConfirmPreview
                    ? `"${deleteConfirmPreview}${deleteConfirmPreview.length >= 120 ? '…' : ''}" will be permanently deleted.`
                    : 'This message will be permanently deleted.', confirmText: "Delete", cancelText: "Cancel", onConfirm: () => void handleConfirmDeleteMessage() })] }));
}
